import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { Contact, Person, SchemaOptions, User } from '@crmsy/shared';
import { daysBetween } from '@crmsy/shared';
import { todayIso } from '../lib/today.js';
import { db } from '../lib/db.js';
import { PRIORITY, useKeys } from '../lib/keyboard.js';
import { useRecordActions } from '../lib/useRecordActions.jsx';
import { Owners } from './Avatar.jsx';
import { Tag } from './Tag.jsx';
import { Timeline } from './Timeline.jsx';
import { useT } from '../i18n/index.js';

export interface ContactPanelProps {
  contact: Contact;
  people: Person[];
  schema: SchemaOptions;
  user: User;
  onClose: () => void;
}

/* The help overlay shows record-context keys while a panel is open. */
let openPanels = 0;
const panelListeners = new Set<(n: number) => void>();
export function usePanelOpen(): boolean {
  const [n, setN] = useState(openPanels);
  useEffect(() => {
    panelListeners.add(setN);
    return () => {
      panelListeners.delete(setN);
    };
  }, []);
  return n > 0;
}
function notePanel(delta: number) {
  openPanels += delta;
  for (const l of panelListeners) l(openPanels);
}

/**
 * Contact panel (§6): editable fields (same letters as the list filters, §7), the
 * timeline with revert, the latest suggested message, "Open in Notion".
 */
export function ContactPanel({ contact: c, people, schema, user, onClose }: ContactPanelProps) {
  const t = useT();
  useEffect(() => {
    notePanel(1);
    return () => notePanel(-1);
  }, []);
  const ra = useRecordActions(c, { mode: 'record', priority: PRIORITY.panel, schema, people, user });
  useKeys(
    {
      Escape: () => {
        if (ra.dialog) return false; // the dialog's own layer handles it
        onClose();
      },
      'Shift+Enter': () => {
        window.open(c.url, '_blank', 'noopener');
      },
    },
    { priority: PRIORITY.panel },
  );
  const pending = useLiveQuery(() => db.outbox.where('contactId').equals(c.id).count(), [c.id], 0);
  const suggestion = useLiveQuery(
    async () => {
      const rows = await db.log.where('contactId').equals(c.id).toArray();
      return rows.filter((e) => e.type === 'suggestion' && !e.archived).sort((a, b) => b.at.localeCompare(a.at))[0] ?? null;
    },
    [c.id],
    null,
  );
  const today = todayIso();
  const due = c.nextContactDate ? daysBetween(today, c.nextContactDate) : null;

  const anchorOf = (e: React.MouseEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: r.left, y: r.bottom + 4 };
  };
  const Row = ({ label, hotkey, onClick, children }: { label: string; hotkey?: string; onClick?: (e: React.MouseEvent) => void; children: React.ReactNode }) => (
    <div className="grid grid-cols-[120px_1fr] items-start gap-2 py-1.5 text-sm">
      <div className="flex items-center gap-1.5 text-muted">
        {label}
        {hotkey && <kbd>{hotkey}</kbd>}
      </div>
      {onClick ? (
        <button type="button" onClick={onClick} className="min-w-0 rounded-md text-left hover:bg-surface" title={t('panel.clickToChange')}>
          {children}
        </button>
      ) : (
        <div className="min-w-0 break-words">{children}</div>
      )}
    </div>
  );
  const link = (v: string | null) =>
    v ? (
      <a className="text-accent-dark underline-offset-2 hover:underline" href={v.startsWith('http') ? v : `https://${v}`} target="_blank" rel="noopener">
        {v}
      </a>
    ) : (
      <span className="text-muted">—</span>
    );
  const text = (v: string | null) => (v ? v : <span className="text-muted">—</span>);

  return (
    <>
      <div className="fixed inset-0 z-20 bg-ink/20" onMouseDown={onClose} />
      <aside aria-label={t('field.contact')} className="fixed inset-y-0 right-0 z-20 flex w-full max-w-xl flex-col border-l border-line bg-paper shadow-2xl">
        <header className="flex items-start gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-semibold">
              {c.companyName || t('panel.noCompany')}
              {pending > 0 && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-warn-ink align-middle" title={t('panel.saving')} />}
              {c.archived && <span className="ml-2 rounded-full bg-surface px-2 py-0.5 text-xs font-normal text-muted">{t('panel.archived')}</span>}
            </h2>
            <div className="truncate text-sm text-muted">
              {c.contact || '—'}
              {c.contactRole ? ` · ${c.contactRole}` : ''}
            </div>
          </div>
          <a className="btn" href={c.url || undefined} target="_blank" rel="noopener" title="Shift+Enter" aria-disabled={!c.url}>
            {t('panel.openInNotion')}
          </a>
          <button type="button" className="btn" onClick={onClose} title="Esc">
            ✕
          </button>
        </header>

        {!c.archived && (
          <div className="flex flex-wrap gap-1.5 border-b border-line px-5 py-2">
            <button type="button" className="btn btn-primary" onClick={() => ra.open('followedUp')}>
              {t('panel.followedUp')} <kbd className="bg-accent-dark text-white">f</kbd>
            </button>
            <button type="button" className="btn" onClick={() => ra.open('message')}>
              {t('panel.message')} <kbd>m</kbd>
            </button>
            <button type="button" className="btn" onClick={(e) => ra.open('snooze', anchorOf(e))}>
              {t('panel.snooze')} <kbd>z</kbd>
            </button>
            <button type="button" className="btn" onClick={() => ra.open('note')}>
              {t('panel.note')} <kbd>c</kbd>
            </button>
            <button type="button" className="btn" onClick={() => ra.open('edit')}>
              {t('panel.edit')} <kbd>e</kbd>
            </button>
            <span className="flex-1" />
            <button type="button" className="btn text-muted" onClick={() => ra.open('archive')} title={t('panel.archive')}>
              🗑
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          <Row label={t('field.stage')} hotkey="s" onClick={(e) => ra.open('stage', anchorOf(e))}>
            <Tag schema={schema} property="Stage" value={c.stage} />
          </Row>
          <Row label={t('field.status')} hotkey="t" onClick={(e) => ra.open('status', anchorOf(e))}>
            <Tag schema={schema} property="Status" value={c.status} />
          </Row>
          <Row label={t('field.nextAction')} hotkey="a" onClick={(e) => ra.open('nextAction', anchorOf(e))}>
            <Tag schema={schema} property="Next Action" value={c.nextAction} extra={c.nextActionExtra.length} />
          </Row>
          <Row label={t('field.owner')} hotkey="o" onClick={(e) => ra.open('owner', anchorOf(e))}>
            <Owners ids={c.owner} people={people} />
          </Row>
          <Row label={t('field.nextContact')} hotkey="d" onClick={(e) => ra.open('date', anchorOf(e))}>
            {c.nextContactDate ? (
              <span className={due !== null && due < 0 ? 'font-medium text-danger' : due === 0 ? 'font-medium text-warn-ink' : ''}>
                {c.nextContactDate}
                {due !== null && ` · ${due < 0 ? t('due.overdue', { n: -due }) : due === 0 ? t('due.today') : t('due.in', { n: due })}`}
              </span>
            ) : (
              <span className="text-muted">—</span>
            )}
          </Row>
          <Row label={t('field.lastContact')}>{text(c.lastContactDate)}</Row>
          <Row label={t('field.email')}>{c.email ? <a className="text-accent-dark hover:underline" href={`mailto:${c.email}`}>{c.email}</a> : text(null)}</Row>
          <Row label={t('field.phone')}>{c.phone ? <a className="text-accent-dark hover:underline" href={`tel:${c.phone}`}>{c.phone}</a> : text(null)}</Row>
          <Row label={t('field.linkedin')}>{link(c.linkedin)}</Row>
          <Row label={t('field.site')}>{link(c.companySite)}</Row>
          <Row label={t('field.leadSource')}>
            <Tag schema={schema} property="Lead Source" value={c.leadSource} />
          </Row>
          <Row label={t('field.industry')}>
            <Tag schema={schema} property="Industry" value={c.industry} />
          </Row>
          {c.description && <Row label={t('field.description')}>{c.description}</Row>}

          {suggestion && (
            <section className="mt-3 rounded-lg border border-line bg-surface px-3 py-2">
              <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted">
                <span>{t('panel.suggested')}</span>
                <button type="button" className="hover:text-accent-dark" onClick={() => void navigator.clipboard?.writeText(suggestion.text)}>
                  {t('message.copy')}
                </button>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm">{suggestion.text}</p>
            </section>
          )}

          <details className="mt-3 rounded-lg border border-line">
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-muted">{t('panel.otherFields')}</summary>
            <div className="px-3 pb-2">
              {Object.entries(c.extra)
                .filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0) && v !== false)
                .map(([k, v]) => (
                  <Row key={k} label={k}>
                    {Array.isArray(v) ? v.join(', ') : String(v)}
                  </Row>
                ))}
            </div>
          </details>

          <section className="mt-5">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">{t('panel.timeline')}</h3>
            <Timeline contactId={c.id} people={people} user={user} />
          </section>
        </div>
        <footer className="flex items-center gap-3 border-t border-line px-5 py-2 text-[11px] text-muted">
          <span>{t('panel.created', { date: c.createdTime.slice(0, 10) })}</span>
          <span>{t('panel.edited', { date: c.lastEditedTime.slice(0, 16).replace('T', ' ') })}</span>
          {c.lastEditedBy && <span>{t('panel.by', { name: people.find((p) => p.id === c.lastEditedBy)?.name ?? t('panel.app') })}</span>}
        </footer>
      </aside>
      {ra.element}
    </>
  );
}
